import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { especialidadesApi } from '../../services/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import type { Especialidad } from '../../types/api';

export function SpecialtiesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingSpecialtyId, setEditingSpecialtyId] = useState<number | null>(null);
  const [editingSpecialty, setEditingSpecialty] = useState<any>(null);
  const [newSpecialty, setNewSpecialty] = useState({
    nombre: ''
  });

  // Filtros y paginación
  const [inputFilters, setInputFilters] = useState({
    nombre: '',
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [filters, setFilters] = useState(inputFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Obtener lista de especialidades con filtros y paginación
  const { data, isLoading } = useQuery<any, Error>({
    queryKey: ['specialties', filters, page, limit],
    queryFn: () => especialidadesApi.getAll({ ...filters, page, limit }),
    staleTime: 1000 * 60
  });
  const specialties: Especialidad[] = data?.data || [];
  const total: number = data?.total || 0;

  // Mutación para crear especialidad
  const createMutation = useMutation({
    mutationFn: especialidadesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      toast.success('Especialidad creada exitosamente');
      setIsAdding(false);
      setNewSpecialty({
        nombre: ''
      });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Mutación para actualizar especialidad
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return especialidadesApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      toast.success('Especialidad actualizada exitosamente');
      setEditingSpecialtyId(null);
      setEditingSpecialty(null);
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleAddNew = () => {
    setIsAdding(true);
  };

  const handleEdit = (specialty: Especialidad) => {
    setEditingSpecialtyId(specialty.id);
    setEditingSpecialty({
      nombre: specialty.nombre || ''
    });
  };

  const handleSaveNew = () => {
    if (!newSpecialty.nombre) {
      toast.error('Por favor, completa el campo obligatorio: Nombre');
      return;
    }
    createMutation.mutate(newSpecialty as any);
  };

  const handleSaveEdit = () => {
    if (!editingSpecialty.nombre) {
      toast.error('Por favor, completa el campo obligatorio: Nombre');
      return;
    }
    updateMutation.mutate({
      id: editingSpecialtyId!,
      data: editingSpecialty
    });
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewSpecialty({
      nombre: ''
    });
  };

  const handleCancelEdit = () => {
    setEditingSpecialtyId(null);
    setEditingSpecialty(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setNewSpecialty(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (field: string, value: any) => {
    setEditingSpecialty((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Cargando especialidades..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('specialties.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Gestiona la información de las especialidades de la clínica
          </p>
        </div>
        <button
          onClick={handleAddNew}
          disabled={isAdding || editingSpecialtyId !== null}
          className="btn-primary disabled:opacity-50"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Agregar Especialidad
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-end bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Nombre</label>
          <input type="text" value={inputFilters.nombre} onChange={e => setInputFilters(f => ({ ...f, nombre: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta desde</label>
          <input type="date" value={inputFilters.fecha_inicio} onChange={e => setInputFilters(f => ({ ...f, fecha_inicio: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta hasta</label>
          <input type="date" value={inputFilters.fecha_fin} onChange={e => setInputFilters(f => ({ ...f, fecha_fin: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <button onClick={() => { setFilters(inputFilters); setPage(1); }} className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white">Buscar</button>
          <button onClick={() => { setInputFilters({ nombre: '', fecha_inicio: '', fecha_fin: '' }); setFilters({ nombre: '', fecha_inicio: '', fecha_fin: '' }); setPage(1); }} className="btn btn-sm ml-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200">Limpiar</button>
        </div>
      </div>

      {/* Tabla de especialidades */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Fila para agregar nueva especialidad */}
              {isAdding && (
                <tr className="bg-blue-50 dark:bg-blue-900/20">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    Auto
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newSpecialty.nombre}
                      onChange={(e) => handleInputChange('nombre', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Nombre *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveNew}
                        disabled={createMutation.isPending}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                        title="Guardar"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelNew}
                        disabled={createMutation.isPending}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                        title="Cancelar"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* Filas de especialidades existentes */}
              {specialties?.map((specialty) => (
                <tr key={specialty.id} className={editingSpecialtyId === specialty.id ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {specialty.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {editingSpecialtyId === specialty.id ? (
                      <input
                        type="text"
                        value={editingSpecialty.nombre}
                        onChange={(e) => handleEditInputChange('nombre', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Nombre *"
                        required
                      />
                    ) : (
                      specialty.nombre
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {editingSpecialtyId === specialty.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                            title="Guardar"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                            title="Cancelar"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleEdit(specialty)}
                          disabled={isAdding || editingSpecialtyId !== null}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50"
                          title="Editar"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!specialties || specialties.length === 0) && !isAdding && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('common.noData')}
            </p>
          </div>
        )}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {specialties.length} de {total} especialidades
        </div>
        <div className="flex items-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="btn btn-xs">Anterior</button>
          <span className="text-sm">Página {page}</span>
          <button disabled={page * limit >= total} onClick={() => setPage(p => p + 1)} className="btn btn-xs">Siguiente</button>
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="input input-xs ml-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
            {[5, 10, 20, 50].map(opt => <option key={opt} value={opt}>{opt} por página</option>)}
          </select>
        </div>
      </div>
    </div>
  );
} 